import React from 'react';

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`animate-pulse bg-muted rounded-lg ${className}`} />
);

export const CardSkeleton = () => (
  <div className="rounded-xl border border-border bg-card p-5 space-y-4">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
    <Skeleton className="h-20 w-full" />
    <div className="flex gap-2">
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
  </div>
);

export const StatSkeleton = () => (
  <div className="rounded-xl border border-border bg-card p-5 space-y-3">
    <Skeleton className="w-10 h-10 rounded-lg" />
    <Skeleton className="h-7 w-20" />
    <Skeleton className="h-4 w-28" />
  </div>
);

export const TableRowSkeleton = ({ cols = 5 }: { cols?: number }) => (
  <div className="flex items-center gap-4 py-3 px-4 border-b border-border">
    {Array.from({ length: cols }).map((_, i) => (
      <Skeleton
        key={i}
        className={`h-4 ${i === 0 ? 'w-32' : i === cols - 1 ? 'w-16' : 'w-24'} ${i === 0 ? '' : 'flex-1'}`}
      />
    ))}
  </div>
);

export const ListSkeleton = ({ count = 5, type = 'card' }: { count?: number; type?: 'card' | 'stat' | 'row' }) => (
  <div className={type === 'row' ? 'space-y-0' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
    {Array.from({ length: count }).map((_, i) =>
      type === 'stat' ? <StatSkeleton key={i} /> :
      type === 'row' ? <TableRowSkeleton key={i} /> :
      <CardSkeleton key={i} />
    )}
  </div>
);

// ============================================================================
// Domain-specific composed skeletons (match real card dimensions)
// ============================================================================

/** Matches the TeacherCard layout on /teachers (image-on-top grid card). */
export const TeacherGridCardSkeleton = () => (
  <div className="bg-card rounded-xl border border-border overflow-hidden h-full flex flex-col">
    {/* Image area — same aspect-[4/3] as the real card */}
    <Skeleton className="aspect-[4/3] w-full rounded-none" />
    <div className="p-5 flex flex-col flex-1 space-y-3">
      {/* Name */}
      <Skeleton className="h-5 w-3/5" />
      {/* Rating row */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-3.5 w-3.5 rounded-full" />
        <Skeleton className="h-3.5 w-10" />
      </div>
      {/* Bio lines */}
      <div className="space-y-2 flex-1">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
      {/* Specialty chips */}
      <div className="flex flex-wrap gap-1.5">
        <Skeleton className="h-6 w-16 rounded-md" />
        <Skeleton className="h-6 w-20 rounded-md" />
        <Skeleton className="h-6 w-14 rounded-md" />
      </div>
      {/* Footer: price + CTA */}
      <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-4 w-20" />
      </div>
    </div>
  </div>
);

/** Matches the CourseCard layout on /courses (image-on-top grid card). */
export const CourseGridCardSkeleton = () => (
  <div className="bg-card rounded-xl border border-border overflow-hidden h-full flex flex-col">
    {/* Thumbnail — same aspect-video as the real card */}
    <Skeleton className="aspect-video w-full rounded-none" />
    <div className="p-5 flex flex-col flex-1 space-y-3">
      {/* Title (2-line height) */}
      <div className="space-y-2">
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-5 w-2/3" />
      </div>
      {/* Description */}
      <div className="space-y-2 flex-1">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      {/* Meta row: duration / lessons / students */}
      <div className="flex gap-3">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-10" />
      </div>
      {/* Footer: price + CTA */}
      <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  </div>
);

/**
 * Matches the dashboard stat-card layout (label/value on one side,
 * icon box on the other). Used by Student/Teacher/Admin dashboards.
 */
export const StatCardSkeleton = () => (
  <div className="rounded-xl border border-border bg-card">
    <div className="pt-5 pb-4 px-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-16" />
        </div>
        <Skeleton className="w-10 h-10 rounded-lg" />
      </div>
    </div>
  </div>
);

/** Matches a notification row (icon, subject, message, timestamp). */
export const NotificationRowSkeleton = () => (
  <div className="flex items-start gap-4 p-4 rounded-xl border border-border bg-card">
    {/* Icon box */}
    <Skeleton className="h-8 w-8 rounded-lg shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      {/* Subject */}
      <Skeleton className="h-4 w-2/5" />
      {/* Body (2 lines) */}
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      {/* Timestamp */}
      <Skeleton className="h-3 w-20" />
    </div>
  </div>
);

export default Skeleton;
